import {
  Component,
  HostBinding,
  Inject,
  OnDestroy,
  OnInit,
} from '@angular/core'
import { ClearwaterTransaction, ExternalTransaction } from '@helios/common-api'
import { transactionsNotApplicableMessage } from '@helios/generic-components'
import {
  ExternalTransactionsPanelHotkeysType,
  HotkeyDestroyFn,
  HotkeysService,
} from '@helios/hotkeys'
import {
  EventType,
  HeliosMessageType,
  SessionBasedEvent,
  SessionMessagingService,
} from '@helios/messaging'
import { BasePanelComponent, LayoutContainer } from '@helios/panel-base'
import {
  RootState,
  selectActiveSession,
  selectExtTranIdsFilter,
  selectExtTransactionsSessionCacheStatus,
  selectFocusedExtTransaction,
  selectFocusedExtTransactions,
  selectIsEnfusionSession,
  selectTransactionCurrencyCodeFilter,
  selectTransactionSecurityIdFilter,
  selectTransactions,
  SessionCacheStatus,
  setExternalTransactions,
  setFocusedSecurities,
  setFocusedSubSleeveAccountId,
  storeSessionExtTxDataAvailableTime,
} from '@helios/state-management'
import { Store } from '@ngrx/store'
import { ComponentContainer } from 'golden-layout'
import { Subject } from 'rxjs'
import {
  distinctUntilChanged,
  filter,
  map,
  take,
  takeUntil,
} from 'rxjs/operators'
import { ExternalTransactionsTaskService } from './services/external-transactions-task.service'

const EXT_TRAN_ID_SOURCE = 'external'

// TODO: remove this in PVAL-14397
export type ExtTranSSEEventPayload = string[] | { rowIds: string[] }

@Component({
  selector: 'hs-external-transactions-panel',
  templateUrl: './external-transactions-panel.component.html',
  styleUrls: ['./external-transactions-panel.component.scss'],
})
export class ExternalTransactionsPanelComponent
  extends BasePanelComponent
  implements OnInit, OnDestroy
{
  selectedExtTrans: ExternalTransaction[] = []
  selectedCwTrans: ClearwaterTransaction[] = []
  lastProcessedSessionId?: string
  lastProcessedAccountIds?: number[]

  /**
   * True when the active session is Enfusion-sourced (`kind === 'ENFN'`). The grid is
   * then never created and a static message card is shown instead of waiting on a
   * session cache that will never become ready for these accounts.
   */
  notApplicable = false
  readonly notApplicableMessage = transactionsNotApplicableMessage

  readonly securityIdFilter = this.store.select(
    selectTransactionSecurityIdFilter,
  )
  readonly currencyFilter = this.store.select(
    selectTransactionCurrencyCodeFilter,
  )

  /**
   * needed for automated testing, so we know when the cache has loaded.
   * value is passed to the cache-load-listener component to control when the grid is displayed
   */
  @HostBinding('attr.data-cy-cache-status')
  sessionCacheStatus = SessionCacheStatus.UNKNOWN

  readonly updatedExternalTransactions = this.messagingService
    .on<ExtTranSSEEventPayload, SessionBasedEvent<ExtTranSSEEventPayload>>(
      EventType.MESSAGE,
      [
        HeliosMessageType.ExternalTransactionUpdate,
        HeliosMessageType.ExternalTransactionDelete,
        HeliosMessageType.ExternalTransactionCreate,
      ],
    )
    .pipe(
      map(e => {
        // TODO: remove this in PVAL-14397
        if (Array.isArray(e.payload)) {
          return e.payload
        }

        const rowIds = e.payload.rowIds.map(id => id.split('/')[0].trim())
        return rowIds
      }),
    )

  readonly extTranIdFilter = this.store.select(selectExtTranIdsFilter).pipe(
    filter(x => x.source !== EXT_TRAN_ID_SOURCE),
    map(x => x.extTranIds),
  )

  readonly focusedExtTranId$ = this.store.select(selectFocusedExtTransaction)
  readonly focusedExtTranIds$ = this.store.select(selectFocusedExtTransactions)

  private readonly _destroyed$ = new Subject<void>() //cleanup object for observables
  private readonly hotkeyDestroyStack: HotkeyDestroyFn[] = []

  constructor(
    @Inject(LayoutContainer) container: ComponentContainer,
    private readonly store: Store<RootState>,
    private readonly messagingService: SessionMessagingService,
    private readonly taskService: ExternalTransactionsTaskService,
    private readonly hotkeysService: HotkeysService<ExternalTransactionsPanelHotkeysType>,
  ) {
    super(container)
  }

  ngOnInit() {
    this.store
      .select(selectIsEnfusionSession)
      .pipe(takeUntil(this._destroyed$), distinctUntilChanged())
      .subscribe(isEnfusionSession => (this.notApplicable = isEnfusionSession))

    this.store
      .select(selectTransactions)
      .pipe(takeUntil(this._destroyed$))
      .subscribe(cwTrans => (this.selectedCwTrans = cwTrans))

    this.store
      .select(selectExtTransactionsSessionCacheStatus)
      .pipe(takeUntil(this._destroyed$))
      .subscribe(status => {
        this.sessionCacheStatus = status
        this.handleSessionCacheStatusUpdate(status)
      })

    this.registerHotkeys()
  }

  ngOnDestroy() {
    this.unregisterHotkeys()

    this._destroyed$.next()
    this._destroyed$.complete()
  }

  onExternalTransactionsSelected(extTrans: ExternalTransaction[]) {
    this.selectedExtTrans = extTrans
    this.store.dispatch(setExternalTransactions(extTrans))

    const selectedTranAccountId = extTrans.find(
      extTx => extTx.accountId != null,
    )?.accountId
    const securityIds: number[] = extTrans
      .map(extTx => extTx.securityId)
      .filter((secId): secId is number => secId != null)

    this.store.dispatch(setFocusedSecurities({ securityIds }))
    this.store.dispatch(
      setFocusedSubSleeveAccountId({
        subSleeveAccountId: selectedTranAccountId,
      }),
    )
  }

  getRegisteredHotkeyCount(): number {
    return this.hotkeyDestroyStack.length
  }

  private registerHotkeys() {
    this.hotkeyDestroyStack.push(
      this.hotkeysService.bind('find-matched-cw-trans', () => {
        this.taskService.findMatchedTransaction(this.selectedExtTrans)
      }),
    )
  }

  private unregisterHotkeys() {
    this.hotkeyDestroyStack.forEach(hotkeyDestroyFn => hotkeyDestroyFn())
    this.hotkeyDestroyStack.length = 0
  }

  handleSessionCacheStatusUpdate(status: SessionCacheStatus): void {
    this.store
      .select(selectActiveSession)
      .pipe(take(1))
      .subscribe(session => {
        if (
          session?.id == null ||
          this.lastProcessedSessionId === session?.id
        ) {
          return
        }

        this.lastProcessedSessionId = session.id
        this.lastProcessedAccountIds = session.loadedAccountIds

        if (
          status === SessionCacheStatus.PARTIAL ||
          status === SessionCacheStatus.DONE
        ) {
          this.store.dispatch(
            storeSessionExtTxDataAvailableTime({
              sessionId: session.id,
              extTxDataAvailableTime: Date.now(),
            }),
          )
        }
      })
  }
}
